import { Injectable } from '@angular/core';
import { PermissionRQ, PermissionUser, RolRQ, RolUser, UserCreate, UserDetail, UserDetailResponse, UserFilters, UserQueryFilter, UserUpdate } from '../../models/user.models';
import { environment } from '../../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, map, tap, throwError } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { SnackbarComponent } from '../../../shared/components/snackbar/snackbar.component';
import { UtilsService } from '../../../shared/services/utils.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private hasChanges: boolean = false;
  private lastQueryFilter: UserQueryFilter = {
    paginateRequest: {
      limit: 0,
      offset: 0
    }
  };
  constructor(private http: HttpClient, private dialog: MatDialog, private utilService: UtilsService) { }

  getAll(limit: number, offset: number, filters?: UserFilters) {
    let requestBody: UserQueryFilter = {
      paginateRequest: {
        limit: limit,
        offset: offset
      }
    };
    if (filters) {
      requestBody = {
        ...requestBody,
        userFilter: {
          ...filters
        }
      }
    }

    this.lastQueryFilter = requestBody;
    return this.http.post<UserDetailResponse>(`${environment.URL_API}searchUsers`, requestBody)
      .pipe(
        map((response: UserDetailResponse) => {
          this.hasChanges = false;
          return response;
        })
      )
  }

  getById(userId: number) {
    return this.http.get<UserDetail>(`${environment.URL_API}user/${userId}`);
  }

  getAllCategories() {
    return this.http.get<string[]>(`${environment.URL_API}user/categories`);
  }

  getAllDocumentTypes() {
    return this.http.get<string[]>(`${environment.URL_API}user/documentTypes`);
  }

  getAllRoles() {
    return this.http.get<RolRQ>(`${environment.URL_API}role`).pipe(
      map((rolRq: RolRQ) => rolRq.roles)
    );
  }

  getAllPermissions() {
    return this.http.get<PermissionRQ>(`${environment.URL_API}permission`).pipe(
      map((permissionRq: PermissionRQ) => permissionRq.permissions)
    );
  }

  /**
   * @param roles Array of roles 
   * @returns Array of permissions
   * @description It is in charge of returning an array of user permissions without repeating
   */
  getPermissionsByRoles(roles: RolUser[] = []) {
    let permissions: PermissionUser[] = [];

    permissions = roles.flatMap((rol: RolUser) => {
      return this.utilService.removeObjectsDuplicated(rol.permissions, 'id');
    })
    
    return permissions;
  }
  

  getLastQueryFilter() {
    return this.lastQueryFilter;
  }

  getHasChanges() {
    return this.hasChanges;
  }

  create(user: UserCreate) {
    return this.http.post<any>(`${environment.URL_API}user`, user).pipe(
      tap(() => this.hasChanges = true),
      tap((response: UserDetail) => {
        this.openSnackBar('success', 'configuration-page.action-dialog.user-create-title',
          'configuration-page.action-dialog.user-create-label', true);
        return response;
      }),
      catchError((error) => {
        this.openSnackBar('error', 'configuration-page.action-dialog.user-create-title-error',
          'configuration-page.action-dialog.user-create-label-error', true);
        return throwError(() => error);
      })
    )
  }

  update(user: UserUpdate, userId: number, updateOAuthUser: boolean) {
    let params = new HttpParams()
      .append('updateOAuthUser', updateOAuthUser)
      .append('userId', userId);

    return this.http.patch<any>(`${environment.URL_API}user`, user, { params: params }).pipe(
      tap(() => this.hasChanges = true),
      tap((response: UserDetail) => {
        this.openSnackBar('success', 'configuration-page.action-dialog.user-update-title',
          'configuration-page.action-dialog.user-update-label', true);
        return response;
      }),
      catchError((err) => {
        this.openSnackBar('error', 'configuration-page.action-dialog.user-update-title-error',
          'configuration-page.action-dialog.user-update-label-error', true);
        return throwError(() => err);
      })
    )
  }


  private openSnackBar(status: string, title: string, message: string, closeWithTimer?: boolean) {
    this.dialog.open(SnackbarComponent, {
      hasBackdrop: false,
      position: {
        top: "60px"
      },
      data: {
        status,
        message,
        title,
        closeWithTimer
      }
    })
  }
}
